import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  List, Table, TableBody, TableCell, TableHead, TableRow, Alert,
} from '@mui/material';
import { ExpandMore, Api } from '@mui/icons-material';
import { SectionHeader, TodoBanner, Done, Todo } from './DocPrimitives';
import {
  API_ENDPOINTS, SUBSCRIPTIONS,
  AUTH_FLOW_DONE, AUTH_FLOW_TODO,
  ERROR_HANDLING_DONE, ERROR_HANDLING_TODO,
  RIDE_API_OPS, DRIVER_API_OPS, RIDER_API_OPS,
  PAYMENT_API_OPS, KYC_API_OPS, CORE_API_OPS,
} from '../../data/docs/api';

function OpsTable({ queries, mutations }: { queries?: { op: string; desc: string }[]; mutations?: { op: string; desc: string }[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: 'grey.50' }}>
          <TableCell width="40%"><strong>Operation</strong></TableCell>
          <TableCell><strong>Description</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {queries && queries.map(({ op, desc }) => (
          <TableRow key={op}>
            <TableCell><code style={{ fontSize: '0.72rem' }}>{op}</code></TableCell>
            <TableCell><Typography variant="caption">{desc}</Typography></TableCell>
          </TableRow>
        ))}
        {mutations && mutations.map(({ op, desc }) => (
          <TableRow key={op} sx={{ bgcolor: 'rgba(99,102,241,0.03)' }}>
            <TableCell><code style={{ fontSize: '0.72rem', color: '#6366f1' }}>{op}</code></TableCell>
            <TableCell><Typography variant="caption">{desc}</Typography></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function ApiTab() {
  return (
    <Box>
      <SectionHeader icon={<Api />} title="API Reference" subtitle="GraphQL APIs via AWS AppSync — one API per domain, all authenticated via Cognito" />

      {/* Endpoints overview */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>API Endpoints</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>API</strong></TableCell>
                <TableCell><strong>Domain</strong></TableCell>
                <TableCell><strong>Auth</strong></TableCell>
                <TableCell><strong>Used by</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {API_ENDPOINTS.map(({ api, domain, auth, usedBy }) => (
                <TableRow key={api}>
                  <TableCell><strong>{api}</strong></TableCell>
                  <TableCell><code style={{ fontSize: '0.75rem' }}>{domain}</code></TableCell>
                  <TableCell><Typography variant="caption">{auth}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{usedBy}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* Ride API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Ride API — Queries & Mutations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            White rows = queries · Purple rows = mutations
          </Typography>
          <OpsTable queries={RIDE_API_OPS.queries} mutations={RIDE_API_OPS.mutations} />
        </AccordionDetails>
      </Accordion>

      {/* Driver API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Driver API — Queries & Mutations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OpsTable queries={DRIVER_API_OPS.queries} mutations={DRIVER_API_OPS.mutations} />
        </AccordionDetails>
      </Accordion>

      {/* Rider API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Rider API — Queries & Mutations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OpsTable queries={RIDER_API_OPS.queries} mutations={RIDER_API_OPS.mutations} />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <code>updateDriverLocation</code> is called by the driver but received by the rider via the <code>onRiderUpdates</code> subscription. This keeps driver location streaming on the Rider API rather than requiring a separate WebSocket connection.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Payment API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Payment API — Queries & Mutations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OpsTable queries={PAYMENT_API_OPS.queries} mutations={PAYMENT_API_OPS.mutations} />
        </AccordionDetails>
      </Accordion>

      {/* KYC API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>KYC API — Queries & Mutations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OpsTable queries={KYC_API_OPS.queries} mutations={KYC_API_OPS.mutations} />
        </AccordionDetails>
      </Accordion>

      {/* Core API */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Core API — Queries (public, API key auth)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <OpsTable queries={CORE_API_OPS.queries} />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              The Core API uses API key authentication — no Cognito session required. It is the first call mobile apps make on startup to bootstrap all other API endpoints and Cognito configuration.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Subscriptions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Real-Time Subscriptions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" mb={2}>
            AppSync WebSocket subscriptions power all real-time features. Subscriptions reconnect automatically with exponential backoff on connection loss.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Subscription</strong></TableCell>
                <TableCell><strong>API</strong></TableCell>
                <TableCell><strong>Delivers</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SUBSCRIPTIONS.map(({ name, api, delivers }) => (
                <TableRow key={name}>
                  <TableCell><code style={{ fontSize: '0.75rem' }}>{name}</code></TableCell>
                  <TableCell><Typography variant="caption">{api}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{delivers}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* Auth flow */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Authentication Flow</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {AUTH_FLOW_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {AUTH_FLOW_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Error handling */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Error Handling Convention</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {ERROR_HANDLING_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {ERROR_HANDLING_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
